/* Roles — Custom Role management (Phase 4)
   Sub-tabs: All Roles | New Role
   Role detail modal: members (user assignment) + permissions */
import { useState, useMemo } from 'react';
import css from '../AdminPage.module.css';
import {
  useAdminRoles,
  useAdminFeatures,
  useAdminUsers,
  useAdminRoleDetail,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
  useAddRoleMember,
  useRemoveRoleMember,
  useToggleRolePermission,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import type { CustomRole } from '../../../types/admin';

type SubTab = 'roles' | 'new-role';

export default function Roles({ embedded = false }: { embedded?: boolean }) {
  const { data: roles = [], isLoading } = useAdminRoles();
  const { data: features = [] } = useAdminFeatures();
  const { data: allUsers = [] } = useAdminUsers();
  const { showToast } = useToast();

  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();
  const addMember = useAddRoleMember();
  const removeMember = useRemoveRoleMember();
  const togglePerm = useToggleRolePermission();

  const [subTab, setSubTab] = useState<SubTab>('roles');
  const [search, setSearch] = useState('');

  /* new role form */
  const [nrName, setNrName] = useState('');
  const [nrDescription, setNrDescription] = useState('');

  /* detail modal */
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const { data: roleDetail } = useAdminRoleDetail(editingRoleId);

  /* member add form (inside modal) */
  const [memberSearch, setMemberSearch] = useState('');

  /* inline rename */
  const [renamingName, setRenamingName] = useState('');
  const [renamingDesc, setRenamingDesc] = useState('');

  const filtered = useMemo(() => {
    if (!search) return roles;
    const q = search.toLowerCase();
    return roles.filter(
      (r) => r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q),
    );
  }, [roles, search]);

  const featuresByCategory = useMemo(() => {
    const map = new Map<string, typeof features>();
    for (const f of features) {
      const cat = f.category || 'General';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(f);
    }
    return map;
  }, [features]);

  const grantedFeatureNames = useMemo(
    () => new Set(roleDetail?.permissions.map((p) => p.feature_name) ?? []),
    [roleDetail],
  );

  const memberUsernames = useMemo(
    () => new Set(roleDetail?.members.map((m) => m.username) ?? []),
    [roleDetail],
  );

  const userSearchResults = useMemo(() => {
    if (!memberSearch.trim()) return [];
    const q = memberSearch.toLowerCase();
    return allUsers
      .filter(
        (u) =>
          u.username.toLowerCase().includes(q) || (u.full_name || '').toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [allUsers, memberSearch]);

  async function handleCreateRole() {
    if (!nrName.trim()) return;
    try {
      await createRole.mutateAsync({
        name: nrName.trim(),
        description: nrDescription.trim() || undefined,
      });
      showToast('Role created', 'success');
      setNrName('');
      setNrDescription('');
      setSubTab('roles');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showToast(msg ?? 'Failed to create role', 'error');
    }
  }

  async function handleDeleteRole(r: CustomRole) {
    if (
      !confirm(`Delete role "${r.name}"? Members will lose permissions inherited from this role.`)
    )
      return;
    try {
      await deleteRole.mutateAsync(r.id);
      showToast('Role deleted', 'success');
    } catch {
      showToast('Failed to delete role', 'error');
    }
  }

  function openEdit(r: CustomRole) {
    setEditingRoleId(r.id);
    setRenamingName(r.name);
    setRenamingDesc(r.description ?? '');
    setMemberSearch('');
  }

  async function handleSaveRename() {
    if (!editingRoleId) return;
    try {
      await updateRole.mutateAsync({
        id: editingRoleId,
        name: renamingName.trim(),
        description: renamingDesc.trim() || undefined,
      });
      showToast('Role updated', 'success');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showToast(msg ?? 'Failed to update role', 'error');
    }
  }

  async function handleAddMember(username: string) {
    if (!editingRoleId) return;
    try {
      await addMember.mutateAsync({ roleId: editingRoleId, username });
      setMemberSearch('');
    } catch {
      showToast('Failed to assign user to role', 'error');
    }
  }

  async function handleRemoveMember(username: string) {
    if (!editingRoleId) return;
    try {
      await removeMember.mutateAsync({ roleId: editingRoleId, username });
    } catch {
      showToast('Failed to remove user from role', 'error');
    }
  }

  async function handleTogglePerm(featureName: string, currentlyGranted: boolean) {
    if (!editingRoleId) return;
    try {
      await togglePerm.mutateAsync({
        roleId: editingRoleId,
        feature_name: featureName,
        grant: !currentlyGranted,
      });
    } catch {
      showToast('Failed to toggle permission', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <>
      <div className={embedded ? undefined : css.section}>
        {!embedded && (
          <div className={css.sectionHeader}>
            <div>
              <h2 className={css.sectionTitle}>🎭 Custom Roles</h2>
              <p className={css.sectionSubtitle}>
                {roles.length} custom role{roles.length !== 1 ? 's' : ''} — permissions stack
                additively with a user's system role
              </p>
            </div>
          </div>
        )}
        {embedded && (
          <p className={css.sectionSubtitle} style={{ marginBottom: 16 }}>
            {roles.length} custom role{roles.length !== 1 ? 's' : ''} — permissions stack additively
            with a user's system role
          </p>
        )}

        {/* ─── System Roles Reference ─── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 12,
            marginBottom: 24,
            padding: '16px',
            background: 'var(--surface-secondary)',
            borderRadius: 'var(--radius)',
            border: '1px solid var(--border-primary)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              gridColumn: '1/-1',
              marginBottom: 4,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Built-in System Roles
          </div>
          {[
            {
              name: 'user',
              badge: '#6B7280',
              title: 'User',
              description:
                'Blank slate — no admin access by default. Can view and work on cases they are assigned to. All feature access must be granted via individual permissions or custom roles.',
            },
            {
              name: 'admin',
              badge: '#3B82F6',
              title: 'Admin',
              description:
                'Standard management access. Can use the admin panel, manage cases, users, and most operational settings. Cannot change firm branding, billing, or system-level configuration.',
            },
            {
              name: 'superadmin',
              badge: '#8B5CF6',
              title: 'Superadmin',
              description:
                'Full unrestricted access. Bypasses all feature permission checks. Can manage firm branding, billing, integrations, and all system settings. Can change admins and below, but not other superadmins or the System Owner.',
            },
            {
              name: 'system_owner',
              badge: '#B45309',
              title: 'System Owner',
              description:
                "The firm's ultimate system-owner role. Can change anyone, including superadmins. Only another System Owner can grant or remove it, and the last one can never be removed.",
            },
          ].map((sr) => (
            <div
              key={sr.name}
              style={{
                background: 'var(--surface-primary)',
                border: '1px solid var(--border-primary)',
                borderRadius: 'var(--radius)',
                padding: '12px 14px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '9999px',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 700,
                    background: sr.badge + '22',
                    color: sr.badge,
                    letterSpacing: '0.03em',
                  }}
                >
                  {sr.name}
                </span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                }}
              >
                {sr.description}
              </p>
            </div>
          ))}
        </div>

        {/* Sub-tabs */}
        <div className={css.tabs} style={{ marginBottom: 16 }}>
          <button
            className={subTab === 'roles' ? css.tabActive : css.tab}
            onClick={() => setSubTab('roles')}
          >
            All Roles ({roles.length})
          </button>
          <button
            className={subTab === 'new-role' ? css.tabActive : css.tab}
            onClick={() => setSubTab('new-role')}
          >
            New Role
          </button>
        </div>

        {/* ─── All Roles Tab ─── */}
        {subTab === 'roles' && (
          <>
            <div className={css.toolbar} style={{ marginBottom: 16 }}>
              <div className={css.searchWrapper}>
                <span className={css.searchIcon}>🔍</span>
                <input
                  className={css.searchInput}
                  placeholder="Search roles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className={css.emptyState}>
                <div className={css.emptyIcon}>🎭</div>
                <p className={css.emptyMessage}>No custom roles found</p>
                <button className={css.btnPrimary} onClick={() => setSubTab('new-role')}>
                  Create First Role
                </button>
              </div>
            ) : (
              <table className={css.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Members</th>
                    <th>Permissions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => openEdit(r)}
                      style={{ cursor: 'pointer' }}
                      title="Click to manage"
                    >
                      <td style={{ fontWeight: 600 }}>{r.name}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)' }}>
                        {r.description || '—'}
                      </td>
                      <td>
                        <span className={css.badgeInfo}>{r.member_count ?? 0}</span>
                      </td>
                      <td>
                        <span className={css.badgeNeutral}>{r.permission_count ?? 0}</span>
                      </td>
                      <td>
                        <button
                          className={`${css.btnDanger} ${css.btnSmall}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRole(r);
                          }}
                          disabled={deleteRole.isPending}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* ─── New Role Tab ─── */}
        {subTab === 'new-role' && (
          <div className={css.card} style={{ maxWidth: 520 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>
              Create New Custom Role
            </h3>
            <p
              style={{
                margin: '0 0 16px',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--gray-500)',
              }}
            >
              Cannot use reserved names: <code>user</code>, <code>auditor</code>, <code>admin</code>
              , <code>superadmin</code>, <code>system_owner</code>
            </p>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Role Name *</label>
              <input
                className={css.formInput}
                value={nrName}
                onChange={(e) => setNrName(e.target.value)}
                placeholder="e.g. Case Handler"
              />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Description</label>
              <input
                className={css.formInput}
                value={nrDescription}
                onChange={(e) => setNrDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <div style={{ marginTop: 16 }}>
              <button
                className={css.btnPrimary}
                onClick={handleCreateRole}
                disabled={!nrName.trim() || createRole.isPending}
              >
                {createRole.isPending ? 'Creating...' : 'Create Role'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Role Detail Modal ── */}
      {editingRoleId !== null && (
        <div className={css.modalBackdrop} onClick={() => setEditingRoleId(null)}>
          <div
            className={css.modalLg || css.modal}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 760, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>
                Manage Role: {roleDetail?.name ?? '…'}
                <span
                  style={{
                    fontWeight: 400,
                    fontSize: 'var(--font-size-sm)',
                    marginLeft: 8,
                    color: 'var(--gray-500)',
                  }}
                >
                  {roleDetail?.members.length ?? 0} members · {roleDetail?.permissions.length ?? 0}{' '}
                  permissions
                </span>
              </h3>
              <button className={css.modalClose} onClick={() => setEditingRoleId(null)}>
                ×
              </button>
            </div>

            <div className={css.modalBody} style={{ overflowY: 'auto', flex: 1 }}>
              {!roleDetail ? (
                <div className={css.spinner}>
                  <div className={css.spinnerDot} />
                  <div className={css.spinnerDot} />
                  <div className={css.spinnerDot} />
                </div>
              ) : (
                <>
                  {/* ── Name / description ── */}
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Name</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        className={css.formInput}
                        value={renamingName}
                        onChange={(e) => setRenamingName(e.target.value)}
                      />
                      <button
                        className={css.btnSecondary}
                        onClick={handleSaveRename}
                        disabled={updateRole.isPending || !renamingName.trim()}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Description</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        className={css.formInput}
                        value={renamingDesc}
                        onChange={(e) => setRenamingDesc(e.target.value)}
                        placeholder="Optional"
                      />
                      <button
                        className={css.btnSecondary}
                        onClick={handleSaveRename}
                        disabled={updateRole.isPending}
                      >
                        Save
                      </button>
                    </div>
                  </div>

                  {/* ── Members ── */}
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Members</label>

                    <div style={{ marginBottom: 8, position: 'relative' }}>
                      <input
                        className={css.formInput}
                        placeholder="Search users to assign…"
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                      />
                      {userSearchResults.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            zIndex: 20,
                            left: 0,
                            right: 0,
                            top: '100%',
                            background: 'var(--surface-primary)',
                            border: '1px solid var(--border-primary)',
                            borderRadius: 'var(--radius)',
                            boxShadow: 'var(--shadow-md)',
                          }}
                        >
                          {userSearchResults.map((u) => (
                            <button
                              key={u.username}
                              style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                padding: '8px 12px',
                                fontSize: 'var(--font-size-sm)',
                                border: 'none',
                                background: 'transparent',
                                color: memberUsernames.has(u.username)
                                  ? 'var(--gray-400)'
                                  : 'var(--text-primary)',
                                cursor: memberUsernames.has(u.username) ? 'default' : 'pointer',
                              }}
                              onClick={() => {
                                if (memberUsernames.has(u.username)) return;
                                handleAddMember(u.username);
                                setMemberSearch('');
                              }}
                              disabled={memberUsernames.has(u.username)}
                            >
                              <strong>{u.username}</strong>
                              {u.full_name && (
                                <span style={{ marginLeft: 6, color: 'var(--gray-500)' }}>
                                  {u.full_name}
                                </span>
                              )}
                              {memberUsernames.has(u.username) && (
                                <span style={{ marginLeft: 6, color: 'var(--gray-400)' }}>
                                  (already assigned)
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {roleDetail.members.length === 0 ? (
                      <p
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                          margin: 0,
                        }}
                      >
                        No members assigned
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {roleDetail.members.map((m) => (
                          <span
                            key={m.username}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '4px 10px',
                              borderRadius: 9999,
                              background: 'var(--primary-50)',
                              border: '1px solid var(--primary-200)',
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--primary-700)',
                            }}
                          >
                            {m.username}
                            <button
                              onClick={() => handleRemoveMember(m.username)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--primary-400)',
                                padding: 0,
                                lineHeight: 1,
                                fontSize: 14,
                              }}
                              title="Remove from role"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── Permissions ── */}
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>
                      Permissions
                      <span
                        style={{
                          fontWeight: 400,
                          marginLeft: 6,
                          color: 'var(--gray-500)',
                          fontSize: 'var(--font-size-xs)',
                        }}
                      >
                        (stacks additively with each member's system role)
                      </span>
                    </label>
                    {[...featuresByCategory.entries()].map(([cat, feats]) => (
                      <div key={cat} style={{ marginBottom: 12 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--gray-500)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: 4,
                          }}
                        >
                          {cat}
                        </div>
                        <div style={{ display: 'grid', gap: 4 }}>
                          {feats.map((f) => {
                            const granted = grantedFeatureNames.has(f.feature_name);
                            return (
                              <label
                                key={f.feature_name}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  padding: '6px 10px',
                                  borderRadius: 'var(--radius-sm)',
                                  background: granted
                                    ? 'var(--primary-50, #eff6ff)'
                                    : 'transparent',
                                  border: `1px solid ${granted ? 'var(--primary-200, #bfdbfe)' : 'var(--gray-200)'}`,
                                  cursor: 'pointer',
                                  fontSize: 'var(--font-size-sm)',
                                  transition: 'all 0.15s',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={granted}
                                  onChange={() => handleTogglePerm(f.feature_name, granted)}
                                  disabled={togglePerm.isPending}
                                />
                                <div>
                                  <div style={{ fontWeight: 500 }}>{f.feature_name}</div>
                                  {f.description && (
                                    <div
                                      style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--gray-500)',
                                      }}
                                    >
                                      {f.description}
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setEditingRoleId(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
